// src/pages/login/Login.tsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { clearAuth, fetchWithAuth } from "../../auth/auth";
import { getApiUrl } from "../../utils/getApiUrl";

import RegisterModal from "../../modal/registerModal/registerModal";
import { GoogleLogin } from "@react-oauth/google";
import { handleGoogleLogin } from "./googleAuth";

import "./login.scss";
import { useSpinner } from "../../components/spinner/spinner";
import { processLoginResponse } from "./loginAction";

const features = [
  {
    icon: "bi-book",
    title: "Smart Dictionary",
    desc: "Look up words with translations, examples, and pronunciation",
  },
  {
    icon: "bi-card-checklist",
    title: "Flashcards",
    desc: "Study with autoplay flashcards and spaced repetition",
  },
  {
    icon: "bi-journal-text",
    title: "Notebooks",
    desc: "Organize vocabulary into custom collections",
  },
  {
    icon: "bi-volume-up",
    title: "Text-to-Speech",
    desc: "Hear correct pronunciation in multiple languages",
  },
];

const Login: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const { showSpinner, hideSpinner } = useSpinner();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    clearAuth();

    if (!email || !password) {
      setError("Enter email and password.");
      return;
    }

    setLoading(true);
    try {
      const resp = await fetchWithAuth(getApiUrl("auth/login"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: email, password }),
      });

      if (!resp.ok) {
        setError("Login failed: incorrect login or password.");
        return;
      }

      const data = await resp.json();
      const success = processLoginResponse({ data, navigate, onError: setError });
      if (!success) return;
    } catch (err: unknown) {
      console.error(err);
      if (err instanceof Error) {
        setError(err.message || "Network error");
      } else {
        setError("An unexpected error occurred.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="login-page">
        {/* ── Left: Branding & Features ── */}
        <div className="login-page__hero">
          <div className="login-page__hero-content">
            {/* Logo */}
            <div className="login-page__logo">
              <div className="login-page__logo-icon">
                <i className="bi bi-book" />
              </div>
              <h1 className="login-page__logo-text">Dictionary</h1>
            </div>

            <p className="login-page__tagline">
              Your personal space to learn, collect&nbsp;&amp; master new&nbsp;words
            </p>

            {/* Feature cards */}
            <div className="login-page__features">
              {features.map((f) => (
                <div className="login-page__feature" key={f.icon}>
                  <div className="login-page__feature-icon">
                    <i className={`bi ${f.icon}`} />
                  </div>
                  <div>
                    <div className="login-page__feature-title">{f.title}</div>
                    <div className="login-page__feature-desc">{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Decorative language snippets */}
            <div className="login-page__langs">
              <span className="login-page__lang-tag">EN</span>
              <span className="login-page__lang-tag">PL</span>
            </div>
          </div>

          {/* Decorative floating words */}
          <div className="login-page__floating" aria-hidden="true">
            <span className="login-page__word login-page__word--1">słownik</span>
            <span className="login-page__word login-page__word--2">learn</span>
            <span className="login-page__word login-page__word--3">Wörter</span>
            <span className="login-page__word login-page__word--4">vocabulary</span>
            <span className="login-page__word login-page__word--5">nauka</span>
          </div>
        </div>

        {/* ── Right: Login Form ── */}
        <div className="login-page__form-wrapper">
          <div className="login-page__form-card">
            <h2 className="login-page__form-title">Dictionary - Sign in</h2>
            <p className="login-page__form-subtitle">
              Welcome back - learn languages anytime!
            </p>

            <form onSubmit={handleSubmit} className="login-page__form">
              {/* Email */}
              <div className="login-page__field">
                <label className="login-page__label" htmlFor="login-email">
                  <i className="bi bi-envelope" /> Email
                </label>
                <input
                  id="login-email"
                  type="email"
                  className="login-page__input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  placeholder="you@example.com"
                  required
                />
              </div>

              {/* Password */}
              <div className="login-page__field">
                <label className="login-page__label" htmlFor="login-password">
                  <i className="bi bi-lock" /> Password
                </label>
                <input
                  id="login-password"
                  type="password"
                  className="login-page__input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  required
                />
              </div>

              {/* Error */}
              {error && (
                <div className="login-page__error">
                  <i className="bi bi-exclamation-circle" /> {error}
                </div>
              )}

              {/* Submit */}
              <button
                className="login-page__submit"
                type="submit"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    />
                    Signing in…
                  </>
                ) : (
                  <>
                    Sign in <i className="bi bi-arrow-right" />
                  </>
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="login-page__divider">
              <span>or continue with</span>
            </div>

            {/* Google */}
            <div className="login-page__google">
              <GoogleLogin
                onSuccess={(res) =>
                  handleGoogleLogin(
                    res.credential,
                    navigate,
                    setError,
                    showSpinner,
                    hideSpinner
                  )
                }
                onError={() => setError("Google login failed")}
              />
            </div>

            {/* Register link */}
            <p className="login-page__register">
              Don't have an account?{" "}
              <button
                type="button"
                className="login-page__register-link"
                onClick={() => setShowRegister(true)}
              >
                Create one
              </button>
            </p>

            {/* Privacy Policy link */}
            <p className="login-page__privacy-policy-link">
              <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>
            </p>
          </div>
        </div>
      </div>

      <RegisterModal
        isOpen={showRegister}
        onClose={() => setShowRegister(false)}
      />
    </>
  );
};

export default Login;